import { UnleashClient } from 'unleash-proxy-client'
// ponytail: medusa uses `import { getToken } from "@/app/infrastructures/misc/Cookies"`;
// cms-customer has no Cookies module — token lives in LocalStorage under TOKEN_KEY.
import { LocalStorage } from '@/app/infrastructures/misc'
// ponytail: medusa uses `import { reactive } from "vue"` (Vue 3 API). cms-customer
// is Vue 2 — equivalent is `Vue.observable` on the imported Vue.
import Vue from 'vue'

// Define state keys as object selector
const StateKey = {
  FCS_CONTEXT: 'fcscontext',
  BCS_CONTEXT: 'bcscontext',
  FCS_TOGGLES: 'fcstoggles',
  BCS_TOGGLES: 'bcstoggles'
} as const

// Environment configuration constants
const ENV_CONFIG = {
  FCS: {
    URL: process.env.VUE_APP_FCS_OPENFEATURE_URL || '',
    CLIENT_KEY: process.env.VUE_APP_FCS_OPENFEATURE_CLIENT_KEY || '',
    APP_NAME: process.env.VUE_APP_FCS_OPENFEATURE_APP_NAME || 'default'
  },
  BCS: {
    URL: process.env.VUE_APP_BCS_OPENFEATURE_URL || '',
    CLIENT_KEY: process.env.VUE_APP_BCS_OPENFEATURE_CLIENT_KEY || '',
    APP_NAME: process.env.VUE_APP_BCS_OPENFEATURE_APP_NAME || 'default'
  }
} as const

// String constants to avoid primitive obsession
const STRING_CONSTANTS = {
  UPDATE_EVENT: 'update',
  EMPTY_STRING: '',
  NAMESPACE_SEPARATOR: '.',
  VUE_PLUGIN_PROPERTIES: {
    UNLEASH_STATE: '$unleashState',
    FCS_FLAG_CLIENT: '$fcsFlagClient',
    BCS_FLAG_CLIENT: '$bcsFlagClient',
    FCS_FLAG: '$fcsFlag',
    BCS_FLAG: '$bcsFlag'
  }
} as const

// Simple flag name type
type FlagName = string

// Define proper types to avoid primitive obsession
interface UnleashState {
  [key: string]: string
  fcscontext: string
  bcscontext: string
  fcstoggles: string
  bcstoggles: string
}

interface FlagContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

interface FlagObject {
  name: string
  setName: (name: FlagName) => void
}

// Client configuration type
interface ClientConfig {
  name: string
  url: string
  clientKey: string
  appName: string
  contextKey: typeof StateKey[keyof typeof StateKey]
  togglesKey: typeof StateKey[keyof typeof StateKey]
}

// Create a reactive state store to trigger rerenders
// ponytail: medusa uses Vue 3 `reactive<T>(...)`; Vue 2 equivalent is `Vue.observable(...)`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const unleashState: UnleashState = (Vue as any).observable({
  [StateKey.FCS_CONTEXT]: '',
  [StateKey.BCS_CONTEXT]: '',
  [StateKey.FCS_TOGGLES]: '',
  [StateKey.BCS_TOGGLES]: ''
})

// Client configurations
const clientConfigs: ClientConfig[] = [
  {
    name: 'fcs',
    url: ENV_CONFIG.FCS.URL,
    clientKey: ENV_CONFIG.FCS.CLIENT_KEY,
    appName: ENV_CONFIG.FCS.APP_NAME,
    contextKey: StateKey.FCS_CONTEXT,
    togglesKey: StateKey.FCS_TOGGLES
  },
  {
    name: 'bcs',
    url: ENV_CONFIG.BCS.URL,
    clientKey: ENV_CONFIG.BCS.CLIENT_KEY,
    appName: ENV_CONFIG.BCS.APP_NAME,
    contextKey: StateKey.BCS_CONTEXT,
    togglesKey: StateKey.BCS_TOGGLES
  }
]

// Create clients dynamically
const clients = new Map<string, typeof UnleashClient>()
clientConfigs.forEach(config => {
  try {
    clients.set(
      config.name,
      new UnleashClient({
        url: config.url,
        clientKey: config.clientKey,
        appName: config.appName
      })
    )
  } catch (error) {
    console.error(`Failed to create Unleash client ${config.name}:`, error)
    return
  }
})

// Export clients for backward compatibility
// ponytail: medusa uses `clients.get("fcs")!` non-null assertion. cms-customer bans
// `!` via lint; we cast to UnleashClient instead — equivalent in effect.
export const fcsFlagClient = clients.get('fcs') as typeof UnleashClient
export const bcsFlagClient = clients.get('bcs') as typeof UnleashClient

const triggerReactive = (client: typeof UnleashClient, config: ClientConfig) => {
  const originalIsEnabled = client.isEnabled
  const originalGetVariant = client.getVariant
  // Override methods to make them reactive
  client.isEnabled = function(name: string): boolean {
    // Access the reactive properties to create dependencies
    const _context = unleashState[config.contextKey]
    const _toggles = unleashState[config.togglesKey]

    const result = originalIsEnabled.call(this, name)

    return result
  }

  client.getVariant = function(name: string) {
    // Access the reactive properties to create dependencies
    const _context = unleashState[config.contextKey]
    const _toggles = unleashState[config.togglesKey]

    const result = originalGetVariant.call(this, name)

    return result
  }
}

// Helper function to make client methods reactive
function makeClientReactive(client: typeof UnleashClient, config: ClientConfig) {
  // Set up listener for updates
  client.on(STRING_CONSTANTS.UPDATE_EVENT, () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const context = client.getContext() as any
    unleashState[config.contextKey] = JSON.stringify(client.getContext())
    unleashState[config.togglesKey] = JSON.stringify(client.getAllToggles())
  })

  triggerReactive(client, config)
}

let isReady = false

export const waitForUnleashReadiness = (isProfileError = false): Promise<void> => {
  return new Promise(resolve => {
    // ponytail: medusa reads token from a Cookie via `getToken()`. cms-customer
    // has no Cookies module — token is in localStorage. Same key (`auth_token`).
    const token = LocalStorage.getLocalStorage(LocalStorage.TOKEN_KEY) as string

    if (!token || isReady || isProfileError) {
      resolve()
      return
    }
    Promise.allSettled(
      clientConfigs.map(config => {
        const client = clients.get(config.name)
        if (!client) {
          return Promise.resolve()
        }
        return new Promise<void>(res => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let timeoutInstance: any = null
          const checkContext = async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const context = client.getContext() as any
            if (context?.email) {
              isReady = true
              client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext)
              clearTimeout(timeoutInstance)
              await client.updateToggles()
              res()
            }
          }
          client.on(STRING_CONSTANTS.UPDATE_EVENT, checkContext)
          timeoutInstance = setTimeout(() => {
            console.log(`Unleash client ${config.name} readiness check timed out.`)
            client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext)
            res()
          }, 10000)
        })
      })
    ).then(() => {
      resolve()
    })
  })
}

// Initialize both clients
export const initUnleashClients = async () => {
  try {
    await Promise.allSettled(
      clientConfigs.map(config => {
        const client = clients.get(config.name)
        if (!client) {
          console.error(`[Unleash Debug] Client ${config.name} not found!`)
          return Promise.resolve()
        }
        makeClientReactive(client, config)
        return client.start()
      })
    )
  } catch (error) {
    console.error('Failed to initialize Unleash clients:', error)
    return
  }
}

// Custom context object for targeting rules
export const updateUnleashContext = (context: FlagContext): void => {
  try {
    clients.forEach(client => {
      client.updateContext(context)
    })
  } catch (error) {
    console.error('Failed to update Unleash context:', error)
    return
  }
}

export const updateContextField = (field: string, value: string): void => {
  try {
    clients.forEach(client => {
      client.setContextField(field, value)
    })
  } catch (error) {
    console.error('Failed to update Unleash context:', error)
    return
  }
}

// Base flag classes with abstraction
abstract class BaseFlag<T = unknown> {
  readonly defaultValue: T
  protected _name: FlagName
  protected client: typeof UnleashClient

  constructor(defaultValue: T, client: typeof UnleashClient, flagName?: FlagName) {
    this.defaultValue = defaultValue
    this.client = client
    this._name = flagName || ''
  }

  get name(): string {
    return this._name
  }

  setName(name: FlagName): void {
    if (!this._name) {
      this._name = name
    }
  }

  unfreeze(): void {
    // No-op for compatibility with Rox
  }
}

class Flag extends BaseFlag<boolean> {
  constructor(
    defaultValue = false,
    client: typeof UnleashClient,
    flagName?: FlagName
  ) {
    super(defaultValue, client, flagName)
  }

  isEnabled(): boolean {
    try {
      const variant = this.client.getVariant(this._name)
      const payloadValue = variant.payload?.value

      if (payloadValue !== undefined && payloadValue !== null) {
        const normalized = String(payloadValue).trim().toLowerCase()
        if (normalized === 'true') return true
        if (normalized === 'false') return false

        const parsedValue = Number(normalized)
        if (!isNaN(parsedValue)) {
          return !!parsedValue
        }
      }

      return this.client.isEnabled(this._name) ?? this.defaultValue
    } catch (error) {
      console.error('Failed to get variant:', error)
      return this.defaultValue
    }
  }
}

class StringFlag extends BaseFlag<string> {
  constructor(defaultValue = '', client: typeof UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName)
  }

  getValue(): string {
    try {
      const variant = this.client.getVariant(this._name)
      return variant.payload?.value ?? this.defaultValue
    } catch (error) {
      console.error('Failed to get variant:', error)
      return this.defaultValue
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class JSONFlag extends BaseFlag<Record<string, any>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(defaultValue = {} as Record<string, any>, client: typeof UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue(): Record<string, any> {
    try {
      const variant = this.client.getVariant(this._name)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return JSON.parse(variant.payload?.value || JSON.stringify(this.defaultValue))
    } catch (error) {
      console.error('Failed to get variant:', error)
      return this.defaultValue
    }
  }
}

class NumberFlag extends BaseFlag<number> {
  constructor(defaultValue = 0, client: typeof UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName)
  }

  getValue(): number {
    try {
      const variant = this.client.getVariant(this._name)
      const variantValue = variant.payload?.value
      if (variantValue === undefined) return this.defaultValue
      const numValue = Number(variantValue)
      return isNaN(numValue) ? this.defaultValue : numValue
    } catch (error) {
      console.error('Failed to get variant:', error)
      return this.defaultValue
    }
  }
}

// Factory function to create flag namespaces
function createFlagNamespace(client: typeof UnleashClient) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const registeredFlags: Record<string, Record<string, unknown>> = {}

  const isValidFlag = (flag: unknown): flag is FlagObject => {
    return (
      flag !== null &&
      flag !== undefined &&
      typeof flag === 'object' &&
      'setName' in (flag as Record<string, unknown>)
    )
  }

  const shouldSetName = (
    flag: FlagObject,
    _namespace: string,
    _key: string
  ): boolean => {
    return (
      flag.name === STRING_CONSTANTS.EMPTY_STRING &&
      typeof flag.setName === 'function'
    )
  }

  const buildFlagName = (namespace: string, key: string): FlagName => {
    return namespace
      ? `${namespace}${STRING_CONSTANTS.NAMESPACE_SEPARATOR}${key}`
      : key
  }

  return {
    register(namespace: string, flagContainer: Record<string, unknown>): void {
      registeredFlags[namespace] = flagContainer

      // Iterate through flags to set their names based on the keys
      for (const key in flagContainer) {
        const flag = flagContainer[key]

        if (isValidFlag(flag) && shouldSetName(flag, namespace, key)) {
          const flagName = buildFlagName(namespace, key)
          flag.setName(flagName)
        }
      }
    },

    Flag: class extends Flag {
      constructor(defaultValue = false, flagName?: FlagName) {
        super(defaultValue, client, flagName)
      }
    },

    StringFlag: class extends StringFlag {
      constructor(defaultValue = '', flagName?: FlagName) {
        super(defaultValue, client, flagName)
      }
    },

    NumberFlag: class extends NumberFlag {
      constructor(defaultValue = 0, flagName?: FlagName) {
        super(defaultValue, client, flagName)
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    JSONFlag: class extends JSONFlag {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      constructor(defaultValue = {} as Record<string, any>, flagName?: FlagName) {
        super(defaultValue, client, flagName)
      }
    },

    isEnabled(flagName: string, defaultValue = false): boolean {
      const result = client.isEnabled(flagName) ?? defaultValue
      return result
    },

    getVariant(flagName: string, defaultValue = ''): string {
      const result = client.getVariant(flagName)?.name ?? defaultValue
      return result
    }
  }
}

// Create flag namespaces using the factory
export const FcsFlag = createFlagNamespace(fcsFlagClient)
export const BcsFlag = createFlagNamespace(bcsFlagClient)

// ponytail: medusa uses Vue 3 `Vue.config.globalProperties[...]`; cms-customer is
// Vue 2, so we use `Vue.prototype[...]` to attach the same properties globally.
export const createUnleashPlugin = () => {
  return {
    install(VueInstance: any) {
      // Make the reactive state accessible
      VueInstance.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.UNLEASH_STATE
      ] = unleashState

      // Provide client access
      VueInstance.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG_CLIENT
      ] = fcsFlagClient
      VueInstance.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG_CLIENT
      ] = bcsFlagClient

      // Provide flag namespace access
      VueInstance.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG
      ] = FcsFlag
      VueInstance.prototype[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG
      ] = BcsFlag
    }
  }
}
